import { useEffect, useState } from "react";
import InnoIcons from "InnoUI/innoicons";
import { folderTypesMatch } from "utils/optionData/utils";
import { canAccessfolderTypes } from "utils/lookUpTable";
import { useNavigate } from "react-router";
import { getFolderRedirectUrl } from "utils/helper";
import { Link, useSearchParams } from "react-router-dom";
import styled from "@emotion/styled";
import theme from "@theme/";

// Styled components for modern design
const FolderHeader = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 12px;
  background: ${(props) =>
    props.isSelected ? "rgba(227, 110, 0, 0.1)" : "rgba(255, 255, 255, 0.6)"};
  backdrop-filter: blur(8px);
  height: 100%;
  padding-left: ${(props) => props.level * 16}px;
  padding-right: 16px;
  border-bottom: 1px solid rgba(227, 110, 0, 0.08);
  transition: all 0.2s ease;
  border-radius: 0 0 8px 8px;
  margin: 0 8px 6px 8px;

  &:hover {
    background: ${(props) =>
      props.isSelected
        ? "rgba(227, 110, 0, 0.15)"
        : "rgba(255, 255, 255, 0.8)"};
    transform: translateX(2px);
    box-shadow: 0 2px 8px rgba(227, 110, 0, 0.1);
  }
`;

const FolderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 0px;
  width: 100%;
  padding: 12px;
`;

const FolderIcon = styled.span`
  font-size: 22px;
  color: #e36e00;
  transition: all 0.2s ease;
  min-width: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const sharedStyles = `
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  line-height: 1.4;
  transition: all 0.2s ease;

  &:hover {
    color: #e36e00;
  }
`;

const FolderName = styled.span`
  ${sharedStyles}
  padding: 0;
`;

const FolderLink = styled(Link)`
  ${sharedStyles}
  text-decoration: none;
  display: inline-block;
  margin-left: 1px;
`;

const FileCount = styled.span`
  font-size: 11px;
  color: #6b7280;
  font-weight: 500;
  margin-left: 4px;
`;

const ArrowIcon = styled.div`
  font-size: 20px;
  color: #e36e00;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  min-width: 20px;

  &:hover {
    transform: scale(1.1);
  }
`;

const ChildrenContainer = styled.div`
  background: rgba(255, 255, 255, 0.3);
  backdrop-filter: blur(4px);
  border-left: 2px solid rgba(227, 110, 0, 0.1);
  margin-left: 8px;
  margin-top: 4px;
`;

const NewBadge = styled.span`
  background: ${theme.colors.themeColor};
  color: white;
  font-size: 8px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 10px;
  margin-left: 6px;
  box-shadow: 0 0 6px rgba(82, 196, 26, 0.6);
  animation: pulse 1.5s infinite;

  @keyframes pulse {
    0% {
      box-shadow: 0 0 6px rgba(196, 142, 26, 0.4);
    }
    50% {
      box-shadow: 0 0 10px rgba(196, 193, 26, 0.9);
    }
    100% {
      box-shadow: 0 0 6px rgba(196, 185, 26, 0.4);
    }
  }
`;

const Folder = ({ folder, level = 0 }) => {
  const [searchParams] = useSearchParams();
  const folderId = searchParams.get("folderId");
  const type = searchParams.get("type");
  const [isOpen, setIsOpen] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState("");

  const navigate = useNavigate();

  const isSelected = folder.id == folderId;

  const toggleOpen = () => setIsOpen(!isOpen);

  const isFolderIdPresent = (currentFolder, idToCheck) => {
    // Check if the current folder's children contain the ID
    if (currentFolder.children?.some((child) => child.id == idToCheck)) {
      return true;
    }

    // Recursively check the children's children
    return currentFolder.children?.some((child) =>
      isFolderIdPresent(child, idToCheck),
    );
  };

  useEffect(() => {
    if (folderId) {
      setIsOpen(isFolderIdPresent(folder, folderId));
    }
  }, [folder, folderId]);

  useEffect(() => {
    if (folderId) {
      setSelectedFolderId(folder.id);
    } else {
      setSelectedFolderId("");
    }
  }, [folderId]);

  const handleClickFolder = (e, folder) => {
    e.stopPropagation();
    if (folder.folderType == 1) {
      return;
    }
    setSelectedFolderId(folder.id);
    const projectId = folder.projectId || "defaultProjectId";
    const redirectUrl = getFolderRedirectUrl(
      folder.folderType,
      projectId,
      folder.id,
      type,
    );
    if (redirectUrl) {
      navigate(redirectUrl); // Only navigate if a valid redirectUrl is returned
    }
  };

  const redirectUrl = getFolderRedirectUrl(
    folder.folderType,
    folder.projectId || "defaultProjectId",
    folder.id,
    type,
  );

  const folderType = folderTypesMatch.find(
    (item) => item.id === folder.folderType,
  );
  const folderIcon = folderType ? folderType.icon : "📁";

  const shouldShowIcon =
    folder.folderType === canAccessfolderTypes.folder &&
    folder.children.length > 0;

  return (
    <div>
      {/* Parent Header */}
      <FolderHeader isSelected={isSelected} level={level} onClick={toggleOpen}>
        {/* Display arrow icons if any condition is satisfied */}
        {shouldShowIcon && (
          <ArrowIcon>
            {isOpen ? (
              <InnoIcons.KeyboardArrowDownIcon />
            ) : (
              <InnoIcons.KeyboardArrowRightIcon />
            )}
          </ArrowIcon>
        )}

        <FolderContent
          onClick={(e) =>
            folder.folderType !== 1 && !shouldShowIcon
              ? handleClickFolder(e, folder)
              : null
          }
        >
          <FolderIcon>{folderIcon}</FolderIcon>

          {folder.folderType !== 1 ? (
            <FolderLink
              to={redirectUrl}
              onClick={(e) => e.stopPropagation()} // Prevent folder toggle
            >
              <FolderName>{folder.name}</FolderName>
            </FolderLink>
          ) : (
            <FolderName>{folder.name}</FolderName>
          )}

          {folder.folderType === 2 && (
            <FileCount>({folder.fileCount})</FileCount>
          )}
          {folder.folderType === 1 && folder.totalFileCount > 0 && (
            <FileCount>({folder.totalFileCount})</FileCount>
          )}
          {folder.isLatestFile && (
            <NewBadge title="New file added recently">NEW</NewBadge>
          )}
        </FolderContent>
      </FolderHeader>

      {/* Children */}
      {isOpen && folder.children.length > 0 && (
        <ChildrenContainer>
          {folder.children.map((child) => (
            <Folder key={child.id} folder={child} level={level + 1} />
          ))}
        </ChildrenContainer>
      )}
    </div>
  );
};

export default Folder;
